import React from "react"
import { Text, TouchableOpacity, Image, StyleSheet } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"

/**
 * Une option cochable sur l'écran — une matière avec son icône, un volume
 * horaire. Toutes les options sont visibles, aucune modale pour les voir ;
 * quand la sélection est au plafond, la chip en trop se grise en silence
 * (`locked`) : le nombre n'est jamais annoncé.
 *
 * `badge` accueille un repère posé sur la chip (« Le plus choisi »).
 * `ghost` est une chip d'action, pas une option : bord pointillé, texte de
 * la marque (« + 8 autres matières »).
 */
const FunnelChoiceChip = ({
    label,
    iconUri,
    selected = false,
    locked = false,
    ghost = false,
    onPress,
    badge,
}) => (
    <TouchableOpacity
        style={[
            styles.chip,
            selected && styles.chipOn,
            locked && styles.chipLocked,
            ghost && styles.chipGhost,
        ]}
        disabled={locked}
        activeOpacity={0.8}
        onPress={onPress}
        accessibilityRole={ghost ? "button" : "checkbox"}
        accessibilityState={
            ghost ? undefined : { checked: selected, disabled: locked }
        }
        accessibilityLabel={label}
    >
        {iconUri ? (
            <Image
                source={{ uri: iconUri }}
                style={styles.icon}
                resizeMode="contain"
            />
        ) : null}
        <Text
            style={[
                styles.text,
                !selected && styles.textOff,
                ghost && styles.textGhost,
            ]}
        >
            {label}
        </Text>
        {selected ? (
            <FontAwesome5
                name="check"
                size={10}
                color={theme.colors.primaryDark || theme.colors.primary}
            />
        ) : null}
        {badge}
    </TouchableOpacity>
)

const styles = StyleSheet.create({
    chip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 7,
        borderWidth: 2,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
        borderRadius: 12,
        paddingVertical: 8,
        paddingHorizontal: 12,
        // Jamais plus large que sa ligne : un nom long (une spécialité du
        // lycée) passe sur deux lignes au lieu de sortir de l'écran.
        maxWidth: "100%",
    },
    chipOn: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    chipLocked: { opacity: 0.45 },
    chipGhost: {
        borderStyle: "dashed",
        borderColor: theme.colors.primary,
        backgroundColor: "transparent",
    },
    icon: { width: 20, height: 20 },
    text: {
        flexShrink: 1,
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.primaryDark || theme.colors.primary,
    },
    textOff: { color: theme.colors.grey },
    textGhost: { color: theme.colors.primary },
})

export default FunnelChoiceChip
